import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardBody, Typography, Input, Button } from '@material-tailwind/react';
import { FaTrash, FaPlus, FaEdit, FaSearch } from 'react-icons/fa';
import { useAuth } from './AuthContext';

const { rootUrl } = require('../../config/config');

const VehicleMakesManager = () => {
    const { user } = useAuth();
    const [makes, setMakes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [form, setForm] = useState({ code: '', name: '', country: '' });

    const resetForm = () => {
        setEditingId(null);
        setForm({ code: '', name: '', country: '' });
        setError('');
    };

    const validateForm = () => {
        if (!form.code.trim()) return 'Make code is required';
        if (!form.name.trim()) return 'Make name is required';
        return '';
    };

    const fetchMakes = async () => {
        try {
            setLoading(true);
            setError('');
            const token = localStorage.getItem('authToken') || 'default-token';
            const res = await axios.get(`${rootUrl}/vehicles/makes`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setMakes(Array.isArray(res.data.data) ? res.data.data : []);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch vehicle makes.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMakes();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            const payload = {
                code: form.code.toUpperCase().trim(),
                name: form.name.trim(),
                country: form.country.trim() || null,
                input_by: user?.username || 'system'
            };

            if (editingId) {
                await axios.put(`${rootUrl}/vehicles/makes/${editingId}`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                await axios.post(`${rootUrl}/vehicles/makes`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            resetForm();
            setShowForm(false);
            fetchMakes();
        } catch (err) {
            console.error(err);
            const apiData = err?.response?.data;
            let msg = editingId ? 'Failed to update make.' : 'Failed to add make.';
            if (apiData?.message) msg = apiData.message;
            setError(msg);
        }
    };

    const startEdit = async (makeData) => {
        setEditingId(makeData.id);
        setForm({ code: makeData.code, name: makeData.name, country: makeData.country || '' });
        setShowForm(true);
    };

    const cancelForm = () => {
        resetForm();
        setShowForm(false);
    };

    const deleteMake = async (id) => {
        if (!window.confirm('Delete this vehicle make? Associated models will also be deleted.')) return;
        try {
            const token = localStorage.getItem('authToken') || 'default-token';
            await axios.delete(`${rootUrl}/vehicles/makes/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchMakes();
        } catch (err) {
            console.error(err);
            setError('Failed to delete vehicle make.');
        }
    };

    const filteredMakes = makes.filter(m =>
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <Card className="mb-6 shadow-lg">
                <CardBody>
                    <Typography variant="h3" className="mb-4 text-gray-900">
                        Vehicle Makes Management
                    </Typography>
                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-300 text-red-700 rounded">
                            {error}
                        </div>
                    )}
                    {!showForm ? (
                        <Button
                            onClick={() => setShowForm(true)}
                            className="bg-brand-primary text-white shadow-none rounded-none flex items-center gap-2"
                        >
                            <FaPlus /> Add New Make
                        </Button>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-gray-100 rounded">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Code *</label>
                                    <Input
                                        value={form.code}
                                        onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                        placeholder="TOYOTA"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Name *</label>
                                    <Input
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        placeholder="Toyota"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-700 mb-1">Country</label>
                                    <Input
                                        value={form.country}
                                        onChange={(e) => setForm({ ...form, country: e.target.value })}
                                        placeholder="Japan"
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button type="submit" className="bg-green-500 text-white shadow-none rounded-none">
                                    {editingId ? 'Update' : 'Add'}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={cancelForm}
                                    className="bg-gray-500 text-white shadow-none rounded-none"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </form>
                    )}
                    <div className="mt-6 mb-4 flex items-center gap-2">
                        <FaSearch className="text-gray-500" />
                        <Input
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {loading ? (
                        <Typography className="text-center py-4">Loading...</Typography>
                    ) : filteredMakes.length === 0 ? (
                        <Typography className="text-center py-4">No makes found</Typography>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-gray-200">
                                        <th className="border px-4 py-2">Code</th>
                                        <th className="border px-4 py-2">Name</th>
                                        <th className="border px-4 py-2">Country</th>
                                        <th className="border px-4 py-2">Status</th>
                                        <th className="border px-4 py-2 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredMakes.map((make) => (
                                        <tr key={make.id} className="border hover:bg-gray-50">
                                            <td className="border px-4 py-2 font-semibold">{make.code}</td>
                                            <td className="border px-4 py-2">{make.name}</td>
                                            <td className="border px-4 py-2">{make.country || '-'}</td>
                                            <td className="border px-4 py-2">
                                                <span
                                                    className={`px-2 py-1 rounded text-xs ${make.is_active
                                                            ? 'bg-green-100 text-green-700'
                                                            : 'bg-red-100 text-red-700'
                                                        }`}
                                                >
                                                    {make.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="border px-4 py-2 text-center flex gap-2 justify-center">
                                                <Button
                                                    size="sm"
                                                    className="bg-blue-500 text-white"
                                                    onClick={() => startEdit(make)}
                                                >
                                                    <FaEdit />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    color="red"
                                                    onClick={() => deleteMake(make.id)}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default VehicleMakesManager;
